export interface SubtitleCue {
  start: number;
  end: number;
  text: string;
}

export interface MergeShortCuesOptions {
  minWords: number;
  maxWords: number;
  maxChars: number;
}

// Defaults mirror the backend ``Settings`` defaults
// (`lyric_line_min_words=3`, `lyric_line_max_words=5`, `lyric_line_max_chars=42`).
// Keeping them in lockstep means an ASS file rendered by the in-house
// pipeline parses to the same cues whether or not the frontend safety net
// fires. The max word count is tuned for phone-sized viewports.
export const DEFAULT_MIN_WORDS = 3;
export const DEFAULT_MAX_WORDS = 5;
export const DEFAULT_MAX_CHARS = 42;

const DIALOGUE_PREFIX = "Dialogue:";

// ASS dialogue rows look like:
//   Dialogue: Layer,Start,End,Style,Name,MarginL,MarginR,MarginV,Effect,Text
// where Text is the final field. Splitting by `,` with a limit preserves any
// commas inside the text payload itself.
const DIALOGUE_FIELD_COUNT = 10;

function parseAssTimestamp(value: string): number | null {
  // ASS uses ``H:MM:SS.cc`` (centiseconds, two digits after the dot).
  const match = /^(\d+):(\d{1,2}):(\d{1,2})(?:\.(\d{1,3}))?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  // Normalise the fractional part to a 0–1 number regardless of digit count
  // so both ``.5`` and ``.50`` resolve to 0.5s.
  const fraction = match[4] ? Number(`0.${match[4]}`) : 0;
  if (minutes >= 60 || seconds >= 60) return null;
  return hours * 3600 + minutes * 60 + seconds + fraction;
}

function stripOverrideTags(text: string): string {
  return text.replace(/\{[^}]*\}/g, "");
}

function unescapeText(text: string): string {
  // ASS line breaks: ``\N`` is a hard break, ``\n`` a soft break, ``\h`` a
  // non-breaking space. The overlay renders plain text only, so collapse all
  // of these to ordinary whitespace.
  return text
    .replace(/\\N/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\h/g, " ");
}

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function mergeTwo(first: SubtitleCue, second: SubtitleCue): SubtitleCue {
  // Preserve the outer timing so the overlay's active-cue picker still
  // finds the merged line at any ``currentTime`` that fell inside either
  // source line. Collapse whitespace introduced by the join so a multi-line
  // ASS cue does not gain blank rows when merged.
  const text = `${first.text} ${second.text}`.replace(/\s+/g, " ").trim();
  return { start: first.start, end: second.end, text };
}

function fitsCaps(
  candidate: SubtitleCue,
  options: Pick<MergeShortCuesOptions, "maxChars" | "maxWords">,
): boolean {
  return (
    candidate.text.length <= options.maxChars &&
    wordCount(candidate.text) <= options.maxWords
  );
}

export function mergeShortCues(
  cues: SubtitleCue[],
  options: Partial<MergeShortCuesOptions> = {},
): SubtitleCue[] {
  const minWords = options.minWords ?? DEFAULT_MIN_WORDS;
  const maxWords = options.maxWords ?? DEFAULT_MAX_WORDS;
  const maxChars = options.maxChars ?? DEFAULT_MAX_CHARS;
  if (minWords <= 1 || cues.length === 0) return [...cues];

  const emitted: SubtitleCue[] = [];
  const pending: SubtitleCue[] = [...cues];
  let idx = 0;
  while (idx < pending.length) {
    const current = pending[idx];
    if (wordCount(current.text) >= minWords) {
      emitted.push(current);
      idx += 1;
      continue;
    }
    // Try forward merge first.
    if (idx + 1 < pending.length) {
      const candidate = mergeTwo(current, pending[idx + 1]);
      if (fitsCaps(candidate, { maxChars, maxWords })) {
        pending[idx + 1] = candidate;
        idx += 1;
        continue;
      }
    }
    // Forward unavailable or would breach a cap: try backward.
    if (emitted.length > 0) {
      const candidate = mergeTwo(emitted[emitted.length - 1], current);
      if (fitsCaps(candidate, { maxChars, maxWords })) {
        emitted[emitted.length - 1] = candidate;
        idx += 1;
        continue;
      }
    }
    // Both merges rejected — keep verbatim rather than breach a cap.
    emitted.push(current);
    idx += 1;
  }
  return emitted;
}

export function parseAssDialogue(
  source: string,
  options: Partial<MergeShortCuesOptions> = {},
): SubtitleCue[] {
  if (!source) return [];
  const cues: SubtitleCue[] = [];
  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line.startsWith(DIALOGUE_PREFIX)) continue;
    const body = line.slice(DIALOGUE_PREFIX.length).trimStart();
    const fields = body.split(",");
    if (fields.length < DIALOGUE_FIELD_COUNT) continue;
    const start = parseAssTimestamp(fields[1]);
    const end = parseAssTimestamp(fields[2]);
    if (start === null || end === null || end <= start) continue;
    const textParts = fields.slice(DIALOGUE_FIELD_COUNT - 1);
    const text = unescapeText(stripOverrideTags(textParts.join(","))).trim();
    if (!text) continue;
    cues.push({ start, end, text });
  }
  // Downstream consumers (notably the SubtitleOverlay's two-row picker)
  // assume cues are sorted by start time. ASS files conventionally are,
  // but the spec does not require it, so enforce the post-condition here.
  cues.sort((a, b) => a.start - b.start || a.end - b.end);
  return mergeShortCues(cues, options);
}
